export type Language = 'nl' | 'en' | 'ar' | 'zh'
export type ExamLanguage = 'nl' | 'en' | 'ar' | 'cn'

export const availableLanguages: Array<{ code: Language; label: string }> = [
  { code: 'nl', label: 'Nederlands' },
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'العربية الفصحى المبسطة' },
  { code: 'zh', label: '简体中文' },
]

export function isLanguage(value: unknown): value is Language {
  return value === 'nl' || value === 'en' || value === 'ar' || value === 'zh'
}

export function normalizeLanguage(value: unknown): Language | null {
  if (isLanguage(value)) return value
  if (typeof value !== 'string') return null
  const normalized = value.trim().toLowerCase().replace('_', '-')
  return normalized === 'cn' || normalized === 'zh-cn' || normalized === 'zh-hans'
    ? 'zh'
    : null
}

export function toExamLanguage(language: Language): ExamLanguage {
  return language === 'zh' ? 'cn' : language
}

export function toHtmlLanguage(language: Language): string {
  return language === 'zh' ? 'zh-Hans' : language
}

export function normalizeHtmlLanguage(value: string | undefined): string | undefined {
  const language = normalizeLanguage(value)
  return language ? toHtmlLanguage(language) : value
}

const nl = {
  common: {
    loading: 'Laden...',
    retry: 'Opnieuw proberen',
    reload: 'Herladen',
    cancel: 'Annuleren',
    back: 'Terug',
    start: 'Start',
    save: 'Opslaan',
    confirm: 'Bevestigen',
    email: 'E-mailadres',
    password: 'Wachtwoord',
    package: 'Pakket',
    unknown: 'Onbekend',
    noPackage: 'Nog geen pakket',
    notAvailable: 'Niet beschikbaar',
    mediaUnavailable: 'Media niet beschikbaar',
    quizQuestionAlt: (number: number) => `Vraag ${number}`,
  },
  nav: {
    dashboard: 'Dashboard',
    aiCoach: 'AI Leercoach',
    videoCourse: 'Videocursus',
    theoryBook: 'Theorieboek',
    notes: 'Notities',
    practiceExams: 'Examens',
    trafficSigns: 'Verkeersborden',
    progress: 'Voortgang',
    account: 'Account',
    orders: 'Bestellingen',
    settings: 'Instellingen',
    downloads: 'Downloads',
    result: 'Uitslag',
    logout: 'Uitloggen',
    login: 'Inloggen',
    order: 'Bestellen',
    menu: 'Menu',
    openMenu: 'Open menu',
    closeMenu: 'Sluit menu',
    openNavigation: 'Open navigatie',
    toggleNavigation: 'Toggle navigatie',
    section: (title: string) => `Sectie ${title}`,
    contact: 'Contact',
  },
  settings: {
    eyebrow: 'Instellingen',
    title: 'Instellingen',
    description: 'Pas je examen, taal en weergave aan. Je voorkeuren worden op dit apparaat bewaard.',
    examSection: 'Examen',
    extraTimeLabel: 'Extra tijd (45 min)',
    extraTimeDescription: 'Geeft 45 minuten in plaats van 30, zoals het CBR-examen met extra tijd.',
    cbrStyleLabel: 'CBR-examenstijl',
    cbrStyleDescription: 'Toont het examen in de stijl van het officiële CBR-examenscherm.',
    enhancedOrderControlsLabel: 'Nieuwe bediening voor sleepvragen',
    enhancedOrderControlsDescription: 'Toon doelen tijdens het slepen en sta verplaatsen en wisselen van cijfers toe.',
    customNumberKeypadLabel: 'CBR-toetsenbord bij invulvragen',
    customNumberKeypadDescription: 'Gebruik in CBR-stijl het zwevende cijfertoetsenbord in plaats van het standaard numerieke veld.',
    blueHotspotSelectionLabel: 'Blauwe selectie bij hotspotvragen',
    blueHotspotSelectionDescription: 'Markeer in CBR-stijl de gekozen afbeelding met de transparante blauwe selectie.',
    blueSingleChoiceSelectionLabel: 'Blauwe selectie bij ja/nee en meerkeuze',
    blueSingleChoiceSelectionDescription: 'Kleur in CBR-stijl de volledige gekozen antwoordregel blauw.',
    blueMultipleChoiceSelectionLabel: 'Blauwe selectie bij multiple response',
    blueMultipleChoiceSelectionDescription: 'Kleur in CBR-stijl iedere geselecteerde antwoordregel blauw met een vinkje.',
    videoAutoplayLabel: 'Video’s automatisch afspelen',
    videoAutoplayDescription: 'Start dynamische video’s automatisch. Uitgeschakeld start je ze zelf met de afspeelknop.',
    languageSection: 'Taal',
    websiteLanguage: 'Taal van de website',
    secondaryLanguageLabel: 'Tweede taal onder de hoofdtaal',
    secondaryLanguageDescription: 'Kies optioneel Nederlands, Engels, Arabisch of Vereenvoudigd Chinees als tweede regel bij vragen en antwoorden.',
    secondaryLanguageOff: 'Uit (één taal)',
    displaySection: 'Weergave',
    darkThemeLabel: 'Donker thema',
    darkThemeDescription: 'Schakel over naar een donkere weergave.',
  },
  exams: {
    eyebrow: 'Examens',
    title: 'Examens',
    intro: 'Start je rit naar je rijbewijs! Kies een examen en gas erop!',
    unavailableForPlan: 'Geen examens beschikbaar voor jouw abonnement in deze taal.',
    sessionExpired: 'Je sessie is verlopen. Log opnieuw in.',
    loadError: 'Kon examens niet laden.',
    noToken: 'Geen token beschikbaar',
    invalidData: 'Onverwachte examengegevens ontvangen.',
    examLabel: 'Examen',
    newExam: 'Nieuw examen',
    planLabel: 'Pakket:',
    notAvailableTitle: 'Niet beschikbaar in je huidige plan.',
    restart: 'Opnieuw starten',
    resume: 'Hervatten',
    statsTotal: 'Totaal',
    statsOpen: 'Open',
    statsPassed: 'Geslaagd',
    statsInProgress: 'Bezig',
    inProgress: (count: number) => `${count} bezig`,
    statusPassed: 'Geslaagd',
    statusFailed: 'Gezakt',
    statusInProgress: 'Bezig',
    statusNotStarted: 'Nog niet gemaakt',
    attempts: (count: number) => `${count} poging${count === 1 ? '' : 'en'}`,
    fallbackTitle: (order: string) => `Examen ${order}`,
  },
  quiz: {
    loading: 'De quiz wordt geladen...',
    notFound: 'Dit examen kon niet worden gevonden.',
    invalidData: 'Ongeldige quizgegevens ontvangen.',
    loadError: 'Kon de quizgegevens niet laden.',
    notLoggedIn: 'Niet ingelogd.',
    mediaLoading: 'Media laden...',
    remainingTime: 'Resterende tijd',
    showRemainingTime: 'Resterende tijd tonen',
    hideRemainingTime: 'Resterende tijd verbergen',
    examTools: 'Examenhulpmiddelen',
    enableSound: 'Geluid inschakelen',
    disableSound: 'Geluid uitschakelen',
    volumeControl: 'Volumeregeling',
    volumeDown: 'Volume lager',
    volumeUp: 'Volume hoger',
    changeFontSize: 'Tekstgrootte wijzigen',
    displaySettings: 'Weergave-instellingen',
    liveAudioLanguage: 'Taal van de voorleesstem',
    liveAudioLanguageDescription: 'Deze keuze staat los van de talen waarin de vraag wordt getoond.',
    questionLayout: 'Indeling van de vraag',
    layoutSide: 'Naast elkaar',
    layoutStacked: 'Onder elkaar',
    goToFirstQuestion: 'Ga naar de eerste vraag',
    goToQuestion: (number: number) => `Ga naar vraag ${number}`,
    goToInfo: 'Ga naar Info',
    overview: 'Overzicht',
    overviewDescription: 'Gebruik dit overzicht om snel naar een vraag te gaan.',
    examOverview: 'Overzicht examen',
    examOverviewDescription: 'Hier kun je zien welke vragen je hebt beantwoord, nog niet hebt beantwoord en gemarkeerd.',
    currentQuestion: 'Huidige vraag',
    questionDrawer: 'Vraaglade',
    questionDrawerDescription: 'Hiermee kun je snel navigeren tussen de vragen.',
    questionDrawerToggleHint: 'Open en sluit vraaglade',
    openQuestionDrawer: 'Vraaglade openen',
    closeQuestionDrawer: 'Vraaglade sluiten',
    marked: 'Gemarkeerd',
    bookmarkGuidance: 'Gebruik bladwijzer om later snel naar deze vraag terug te keren.',
    unanswered: 'Onbeantwoord',
    answered: 'Beantwoord',
    submitExam: 'Examen inleveren',
    closeExamTitle: 'Examen afsluiten?',
    closeExamDescription: 'Je kunt je voortgang opslaan om later verder te gaan.',
    saveAndClose: 'Opslaan en afsluiten',
    closeWithoutSaving: 'Afsluiten zonder opslaan',
    theoryB: 'B Theorie',
    mark: 'Markeren',
    close: 'Sluiten',
    questionCount: (current: number, total: number) => `Vraag ${current}/${total}`,
    questionNumber: (current: number) => `Vraag ${current}`,
    questionOfTotal: (current: number, total: number) => `Vraag ${current} van ${total}`,
    multipleAnswers: 'Meerdere antwoorden kunnen goed zijn!',
    orderInstruction: 'Sleep de cijfers naar de juiste plek. Houd een geplaatst cijfer vast om het te verplaatsen.',
    orderInstructionClassic: 'Sleep de cijfers naar de juiste plek (tik op een geplaatst cijfer om het terug te zetten).',
    orderAllPlaced: 'Alle cijfers geplaatst',
    singleOrderInstruction: 'Sleep het vinkje naar de juiste plek. Houd het vinkje vast om het te verplaatsen.',
    singleOrderPlaced: 'Vinkje geplaatst',
    numberInputLabel: 'Numeriek antwoord',
    numberKeypadLabel: 'Numeriek toetsenbord',
    numberBackspace: 'Laatste cijfer verwijderen',
    numberMoveKeypad: 'Toetsenbord verplaatsen',
    numberMinus: 'Minteken invoeren',
    numberHideKeypad: 'Toetsenbord verbergen',
    numberDecimalSeparator: ',',
    dynamicPlayInstruction: 'Tik op play om het filmpje te starten.',
    dynamicPlay: 'Video afspelen',
    dynamicPause: 'Video pauzeren',
    questionLoading: 'Vraag wordt geladen...',
    previous: 'Vorige',
    next: 'Volgende',
    liveStartTitle: 'LIVE-examen klaarzetten',
    liveStartDescription: 'Het examen begint bij vraag 1 en leest iedere vraag automatisch voor.',
    liveStart: 'LIVE-examen starten',
    liveLoadingAudio: 'Audio laden...',
    liveNarrating: 'De vraag wordt voorgelezen',
    liveAnswerTime: 'Tijd om te antwoorden',
    liveCorrectAnswer: 'Het juiste antwoord',
    liveNextQuestion: 'Volgende vraag',
    livePaused: 'Gepauzeerd',
    livePause: 'Pauzeren',
    liveResume: 'Hervatten',
    liveAudioError: 'De audio kon niet worden geladen. Het LIVE-examen is gepauzeerd.',
    liveRetry: 'Audio opnieuw proberen',
    liveCloseDescription: 'Je voortgang en antwoorden worden niet opgeslagen.',
    liveCompleteTitle: 'LIVE-examen voltooid',
    liveCompleteDescription: 'Je hebt alle LIVE-vragen doorlopen. Er is geen resultaat opgeslagen.',
    liveRestart: 'Opnieuw starten',
    submit: 'Inleveren',
    info: 'Info',
    finalQuestionMessage: 'Dit was de laatste vraag van je examen.',
    submitNextScreenMessage: 'In het volgende scherm lever je het examen in. Je ziet daarna of je geslaagd bent.',
    confirmSubmitTitle: 'Weet je zeker dat je het examen wil inleveren?',
    submitTimeRemaining: (minutes: number, seconds: number) => `Je hebt ${minutes} minuten en ${seconds} seconden over`,
    submitBookmarkCount: (count: number) => `Je hebt ${count} bladwijzer${count === 1 ? '' : 's'} gemaakt`,
    submitUnansweredCount: (count: number) => `Je hebt ${count} ${count === 1 ? 'vraag' : 'vragen'} niet ingevuld`,
    returnToExam: 'Terug naar examen',
    submitDefinitely: 'Definitief inleveren',
    previousQuestion: 'Vorige vraag',
    nextQuestion: 'Volgende vraag',
  },
  result: {
    title: 'Uitslag',
    score: (correct: number, total: number) => `${correct}/${total} Goed`,
    passed: 'Geslaagd!',
    failed: 'Gezakt',
    notAnswered: 'Niet beantwoord',
    yourAnswer: 'Jouw antwoord:',
    correctAnswer: 'Het juiste antwoord:',
    redoExam: 'Examen opnieuw maken',
  },
  quizzes: {
    title: 'Quizzes',
    syncDisabled: 'Niet ingelogd - synchronisatie met server is uit.',
    incompleteLoadError: 'Kon incomplete quizzes niet laden.',
    progressResumed: (name: string) => `Poging hervat voor "${name}".`,
    progressSaved: 'Voortgang opgeslagen in de cloud.',
    submitted: (correct: number, total: number, percentage: number) => `Ingeleverd! Score: ${correct}/${total} (${percentage}%).`,
    submitFailed: 'Inleveren mislukt (server). Resultaat is lokaal afgerond.',
    continueWhereLeftOff: 'Doorgaan waar je was gebleven',
  },
  downloads: {
    eyebrow: 'Materiaal',
    title: 'Downloads',
    description: 'Je lesmateriaal en samenvattingen verschijnen hier zodra ze beschikbaar zijn.',
    empty: 'Nog geen downloads. Kom binnenkort terug voor extra materiaal.',
  },
  account: {
    eyebrow: 'Account',
    title: 'Mijn gegevens',
    description: 'Hieronder zie je alle gebruikersdata die wij hebben verzameld.',
    loadError: 'Kon accountgegevens niet laden.',
  },
  auth: {
    loginTitle: 'Inloggen',
    emailRequired: 'Vul je e-mailadres in',
    passwordRequired: 'Vul je wachtwoord in',
    forgotPassword: 'Wachtwoord vergeten?',
    expiredAccess: 'Je toegang is verlopen. Kies een pakket om te verlengen.',
    loginFailed: 'Inloggen mislukt.',
    renewAccess: 'Verleng je toegang',
    busy: 'Bezig...',
    forgotTitle: 'Wachtwoord vergeten',
    validEmailRequired: 'Vul een geldig e-mailadres in.',
    codeSent: 'Er is een code naar je e-mail gestuurd.',
    codeRequestError: 'Er is een fout opgetreden bij het aanvragen van de code.',
    send: 'Versturen',
    sending: 'Versturen...',
    enterCodeTitle: 'Code invoeren',
    codeLabel: '4-cijferige code',
    newPassword: 'Nieuw wachtwoord',
    invalidCode: 'Voer een geldige 4-cijferige code in.',
    shortPassword: 'Gebruik een wachtwoord van minimaal 4 tekens.',
    passwordChanged: 'Je wachtwoord is succesvol gewijzigd!',
    resetError: 'Ongeldige code of fout bij het instellen van het wachtwoord.',
    saving: 'Opslaan...',
    setPassword: 'Wachtwoord instellen',
  },
}

const en: typeof nl = {
  common: {
    loading: 'Loading...',
    retry: 'Try again',
    reload: 'Reload',
    cancel: 'Cancel',
    back: 'Back',
    start: 'Start',
    save: 'Save',
    confirm: 'Confirm',
    email: 'Email address',
    password: 'Password',
    package: 'Package',
    unknown: 'Unknown',
    noPackage: 'No package yet',
    notAvailable: 'Not available',
    mediaUnavailable: 'Media unavailable',
    quizQuestionAlt: (number) => `Question ${number}`,
  },
  nav: {
    dashboard: 'Dashboard',
    aiCoach: 'AI Coach',
    videoCourse: 'Video course',
    theoryBook: 'Theory book',
    notes: 'Notes',
    practiceExams: 'Exams',
    trafficSigns: 'Traffic signs',
    progress: 'Progress',
    account: 'Account',
    orders: 'Orders',
    settings: 'Settings',
    downloads: 'Downloads',
    result: 'Result',
    logout: 'Log out',
    login: 'Log in',
    order: 'Order',
    menu: 'Menu',
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    openNavigation: 'Open navigation',
    toggleNavigation: 'Toggle navigation',
    section: (title) => `Section ${title}`,
    contact: 'Contact',
  },
  settings: {
    eyebrow: 'Settings',
    title: 'Settings',
    description: 'Adjust your exam, language, and display preferences. Your choices are saved on this device.',
    examSection: 'Exam',
    extraTimeLabel: 'Extra time (45 min)',
    extraTimeDescription: 'Gives you 45 minutes instead of 30, like the CBR exam with extra time.',
    cbrStyleLabel: 'CBR exam style',
    cbrStyleDescription: 'Shows the exam in the style of the official CBR exam screen.',
    enhancedOrderControlsLabel: 'New drag-question controls',
    enhancedOrderControlsDescription: 'Reveal targets while dragging and allow placed numbers to move and swap.',
    customNumberKeypadLabel: 'CBR keypad for number questions',
    customNumberKeypadDescription: 'In CBR style, use the floating number pad instead of the standard numeric field.',
    blueHotspotSelectionLabel: 'Blue hotspot selection',
    blueHotspotSelectionDescription: 'In CBR style, highlight the selected image with the translucent blue treatment.',
    blueSingleChoiceSelectionLabel: 'Blue yes/no and single-choice selection',
    blueSingleChoiceSelectionDescription: 'In CBR style, color the full selected answer row blue.',
    blueMultipleChoiceSelectionLabel: 'Blue multiple-response selection',
    blueMultipleChoiceSelectionDescription: 'In CBR style, color each selected answer row blue and show a checkmark.',
    videoAutoplayLabel: 'Autoplay videos',
    videoAutoplayDescription: 'Start dynamic videos automatically. When disabled, you start them with the play button.',
    languageSection: 'Language',
    websiteLanguage: 'Website language',
    secondaryLanguageLabel: 'Second language below primary',
    secondaryLanguageDescription: 'Optionally show Dutch, English, Arabic, or Simplified Chinese beneath questions and answers.',
    secondaryLanguageOff: 'Off (one language)',
    displaySection: 'Display',
    darkThemeLabel: 'Dark theme',
    darkThemeDescription: 'Switch to a dark display.',
  },
  exams: {
    eyebrow: 'Exams',
    title: 'Exams',
    intro: 'Start your road to your licence! Choose an exam and get going.',
    unavailableForPlan: 'No exams are available for your subscription in this language.',
    sessionExpired: 'Your session has expired. Please log in again.',
    loadError: 'Could not load exams.',
    noToken: 'No token available',
    invalidData: 'Unexpected exam data received.',
    examLabel: 'Exam',
    newExam: 'New exam',
    planLabel: 'Package:',
    notAvailableTitle: 'Not available in your current plan.',
    restart: 'Restart',
    resume: 'Resume',
    statsTotal: 'Total',
    statsOpen: 'Open',
    statsPassed: 'Passed',
    statsInProgress: 'In progress',
    inProgress: (count) => `${count} in progress`,
    statusPassed: 'Passed',
    statusFailed: 'Failed',
    statusInProgress: 'In progress',
    statusNotStarted: 'Not started',
    attempts: (count) => `${count} attempt${count === 1 ? '' : 's'}`,
    fallbackTitle: (order) => `Exam ${order}`,
  },
  quiz: {
    loading: 'Loading the quiz...',
    notFound: 'This exam could not be found.',
    invalidData: 'Invalid quiz data received.',
    loadError: 'Could not load the quiz data.',
    notLoggedIn: 'Not logged in.',
    mediaLoading: 'Loading media...',
    remainingTime: 'Time remaining',
    showRemainingTime: 'Show remaining time',
    hideRemainingTime: 'Hide remaining time',
    examTools: 'Exam tools',
    enableSound: 'Enable sound',
    disableSound: 'Disable sound',
    volumeControl: 'Volume control',
    volumeDown: 'Volume down',
    volumeUp: 'Volume up',
    changeFontSize: 'Change text size',
    displaySettings: 'Display settings',
    liveAudioLanguage: 'Narration language',
    liveAudioLanguageDescription: 'This choice is independent of the languages used to display the question.',
    questionLayout: 'Question layout',
    layoutSide: 'Side by side',
    layoutStacked: 'Stacked',
    goToFirstQuestion: 'Go to the first question',
    goToQuestion: (number) => `Go to question ${number}`,
    goToInfo: 'Go to Info',
    overview: 'Overview',
    overviewDescription: 'Use this overview to quickly jump to a question.',
    examOverview: 'Exam overview',
    examOverviewDescription: 'See which questions you have answered, not yet answered, and marked.',
    currentQuestion: 'Current question',
    questionDrawer: 'Question drawer',
    questionDrawerDescription: 'Use this to navigate quickly between questions.',
    questionDrawerToggleHint: 'Open and close question drawer',
    openQuestionDrawer: 'Open question drawer',
    closeQuestionDrawer: 'Close question drawer',
    marked: 'Marked',
    bookmarkGuidance: 'Use the bookmark to quickly return to this question later.',
    unanswered: 'Unanswered',
    answered: 'Answered',
    submitExam: 'Submit exam',
    closeExamTitle: 'Close exam?',
    closeExamDescription: 'You can save your progress and continue later.',
    saveAndClose: 'Save and close',
    closeWithoutSaving: 'Close without saving',
    theoryB: 'B Theory',
    mark: 'Mark',
    close: 'Close',
    questionCount: (current, total) => `Question ${current}/${total}`,
    questionNumber: (current) => `Question ${current}`,
    questionOfTotal: (current, total) => `Question ${current} of ${total}`,
    multipleAnswers: 'Multiple answers can be correct.',
    orderInstruction: 'Drag the numbers to the correct position. Hold a placed number to move it.',
    orderInstructionClassic: 'Drag the numbers to the correct position (tap a placed number to return it).',
    orderAllPlaced: 'All numbers placed',
    singleOrderInstruction: 'Drag the checkmark to the correct position. Hold it to move it.',
    singleOrderPlaced: 'Checkmark placed',
    numberInputLabel: 'Numeric answer',
    numberKeypadLabel: 'Numeric keypad',
    numberBackspace: 'Delete last digit',
    numberMoveKeypad: 'Move keypad',
    numberMinus: 'Enter minus sign',
    numberHideKeypad: 'Hide keypad',
    numberDecimalSeparator: '.',
    dynamicPlayInstruction: 'Tap play to start the video.',
    dynamicPlay: 'Play video',
    dynamicPause: 'Pause video',
    questionLoading: 'Loading question...',
    previous: 'Previous',
    next: 'Next',
    liveStartTitle: 'Prepare the LIVE exam',
    liveStartDescription: 'The exam starts at question 1 and reads every question aloud automatically.',
    liveStart: 'Start LIVE exam',
    liveLoadingAudio: 'Loading audio...',
    liveNarrating: 'Reading the question aloud',
    liveAnswerTime: 'Time to answer',
    liveCorrectAnswer: 'Correct answer',
    liveNextQuestion: 'Next question',
    livePaused: 'Paused',
    livePause: 'Pause',
    liveResume: 'Resume',
    liveAudioError: 'The audio could not be loaded. The LIVE exam has been paused.',
    liveRetry: 'Retry audio',
    liveCloseDescription: 'Your progress and answers will not be saved.',
    liveCompleteTitle: 'LIVE exam complete',
    liveCompleteDescription: 'You have completed all LIVE questions. No result was saved.',
    liveRestart: 'Restart',
    submit: 'Submit',
    info: 'Info',
    finalQuestionMessage: 'This was the final question of your exam.',
    submitNextScreenMessage: 'On the next screen you will submit the exam. You will then see whether you passed.',
    confirmSubmitTitle: 'Are you sure you want to submit the exam?',
    submitTimeRemaining: (minutes, seconds) => `You have ${minutes} minutes and ${seconds} seconds remaining`,
    submitBookmarkCount: (count) => `You created ${count} bookmark${count === 1 ? '' : 's'}`,
    submitUnansweredCount: (count) => `You have ${count} unanswered question${count === 1 ? '' : 's'}`,
    returnToExam: 'Return to exam',
    submitDefinitely: 'Submit definitively',
    previousQuestion: 'Previous question',
    nextQuestion: 'Next question',
  },
  result: {
    title: 'Result',
    score: (correct, total) => `${correct}/${total} correct`,
    passed: 'Passed!',
    failed: 'Failed',
    notAnswered: 'Not answered',
    yourAnswer: 'Your answer:',
    correctAnswer: 'Correct answer:',
    redoExam: 'Redo exam',
  },
  quizzes: {
    title: 'Quizzes',
    syncDisabled: 'Not logged in - server sync is off.',
    incompleteLoadError: 'Could not load incomplete quizzes.',
    progressResumed: (name) => `Attempt resumed for "${name}".`,
    progressSaved: 'Progress saved to the cloud.',
    submitted: (correct, total, percentage) => `Submitted! Score: ${correct}/${total} (${percentage}%).`,
    submitFailed: 'Submit failed on the server. The result was completed locally.',
    continueWhereLeftOff: 'Continue where you left off',
  },
  downloads: {
    eyebrow: 'Materials',
    title: 'Downloads',
    description: 'Your lesson materials and summaries will appear here as soon as they are available.',
    empty: 'No downloads yet. Check back soon for extra materials.',
  },
  account: {
    eyebrow: 'Account',
    title: 'My details',
    description: 'Below you can see the user data we have collected.',
    loadError: 'Could not load account details.',
  },
  auth: {
    loginTitle: 'Log in',
    emailRequired: 'Enter your email address',
    passwordRequired: 'Enter your password',
    forgotPassword: 'Forgot password?',
    expiredAccess: 'Your access has expired. Choose a package to renew.',
    loginFailed: 'Login failed.',
    renewAccess: 'Renew your access',
    busy: 'Working...',
    forgotTitle: 'Forgot password',
    validEmailRequired: 'Enter a valid email address.',
    codeSent: 'A code has been sent to your email.',
    codeRequestError: 'Something went wrong while requesting the code.',
    send: 'Send',
    sending: 'Sending...',
    enterCodeTitle: 'Enter code',
    codeLabel: '4-digit code',
    newPassword: 'New password',
    invalidCode: 'Enter a valid 4-digit code.',
    shortPassword: 'Use a password of at least 4 characters.',
    passwordChanged: 'Your password was changed successfully.',
    resetError: 'Invalid code or error while setting the password.',
    saving: 'Saving...',
    setPassword: 'Set password',
  },
}

const ar: typeof nl = {
  common: {
    loading: 'جارٍ التحميل...',
    retry: 'إعادة المحاولة',
    reload: 'إعادة التحميل',
    cancel: 'إلغاء',
    back: 'رجوع',
    start: 'ابدأ',
    save: 'حفظ',
    confirm: 'تأكيد',
    email: 'البريد الإلكتروني',
    password: 'كلمة المرور',
    package: 'الباقة',
    unknown: 'غير معروف',
    noPackage: 'لا توجد باقة بعد',
    notAvailable: 'غير متاح',
    mediaUnavailable: 'الوسائط غير متاحة',
    quizQuestionAlt: (number) => `السؤال ${number}`,
  },
  nav: {
    dashboard: 'لوحة التحكم',
    aiCoach: 'مدرب التعلم الذكي',
    videoCourse: 'دورة الفيديو',
    theoryBook: 'كتاب النظري',
    notes: 'ملاحظات',
    practiceExams: 'الامتحانات',
    trafficSigns: 'إشارات المرور',
    progress: 'التقدم',
    account: 'الحساب',
    orders: 'الطلبات',
    settings: 'الإعدادات',
    downloads: 'التنزيلات',
    result: 'النتيجة',
    logout: 'تسجيل الخروج',
    login: 'تسجيل الدخول',
    order: 'اطلب الآن',
    menu: 'القائمة',
    openMenu: 'فتح القائمة',
    closeMenu: 'إغلاق القائمة',
    openNavigation: 'فتح التنقل',
    toggleNavigation: 'تبديل التنقل',
    section: (title) => `قسم ${title}`,
    contact: 'اتصل بنا',
  },
  settings: {
    eyebrow: 'الإعدادات',
    title: 'الإعدادات',
    description: 'عدّل إعدادات الامتحان واللغة والعرض. تُحفظ اختياراتك على هذا الجهاز.',
    examSection: 'الامتحان',
    extraTimeLabel: 'وقت إضافي (45 دقيقة)',
    extraTimeDescription: 'يمنحك 45 دقيقة بدلًا من 30 دقيقة، مثل امتحان CBR مع الوقت الإضافي.',
    cbrStyleLabel: 'نمط امتحان CBR',
    cbrStyleDescription: 'يعرض الامتحان بأسلوب شاشة امتحان CBR الرسمية.',
    enhancedOrderControlsLabel: 'تحكم جديد لأسئلة السحب',
    enhancedOrderControlsDescription: 'يُظهر أماكن الإسقاط أثناء السحب ويسمح بنقل الأرقام وتبديلها.',
    customNumberKeypadLabel: 'لوحة أرقام CBR لأسئلة الإدخال',
    customNumberKeypadDescription: 'في نمط CBR، استخدم لوحة الأرقام العائمة بدل حقل الأرقام العادي.',
    blueHotspotSelectionLabel: 'تحديد أزرق لأسئلة النقاط الساخنة',
    blueHotspotSelectionDescription: 'في نمط CBR، ميّز الصورة المختارة بطبقة زرقاء شفافة.',
    blueSingleChoiceSelectionLabel: 'تحديد أزرق لأسئلة نعم/لا والاختيار الواحد',
    blueSingleChoiceSelectionDescription: 'في نمط CBR، لوّن صف الإجابة المختارة بالكامل بالأزرق.',
    blueMultipleChoiceSelectionLabel: 'تحديد أزرق لأسئلة الإجابات المتعددة',
    blueMultipleChoiceSelectionDescription: 'في نمط CBR، لوّن كل إجابة مختارة بالأزرق وأظهر علامة صح.',
    videoAutoplayLabel: 'تشغيل الفيديو تلقائيًا',
    videoAutoplayDescription: 'ابدأ مقاطع الفيديو الديناميكية تلقائيًا. عند إيقافه تبدأ الفيديو بزر التشغيل.',
    languageSection: 'اللغة',
    websiteLanguage: 'لغة الموقع',
    secondaryLanguageLabel: 'اللغة الثانية تحت اللغة الأساسية',
    secondaryLanguageDescription: 'اختر الهولندية أو الإنجليزية أو العربية أو الصينية المبسطة لعرضها تحت الأسئلة والإجابات.',
    secondaryLanguageOff: 'إيقاف (لغة واحدة)',
    displaySection: 'العرض',
    darkThemeLabel: 'الوضع الداكن',
    darkThemeDescription: 'التبديل إلى العرض الداكن.',
  },
  exams: {
    eyebrow: 'الامتحانات',
    title: 'الامتحانات',
    intro: 'ابدأ طريقك نحو رخصة القيادة! اختر امتحانًا وابدأ.',
    unavailableForPlan: 'لا توجد امتحانات متاحة لباقتك بهذه اللغة.',
    sessionExpired: 'انتهت جلستك. يرجى تسجيل الدخول مرة أخرى.',
    loadError: 'تعذر تحميل الامتحانات.',
    noToken: 'رمز الدخول غير متاح',
    invalidData: 'تم استلام بيانات امتحان غير متوقعة.',
    examLabel: 'امتحان',
    newExam: 'امتحان جديد',
    planLabel: 'الباقة:',
    notAvailableTitle: 'غير متاح ضمن باقتك الحالية.',
    restart: 'إعادة البدء',
    resume: 'متابعة',
    statsTotal: 'المجموع',
    statsOpen: 'مفتوح',
    statsPassed: 'ناجح',
    statsInProgress: 'قيد التقدم',
    inProgress: (count) => `${count} قيد التقدم`,
    statusPassed: 'ناجح',
    statusFailed: 'راسب',
    statusInProgress: 'قيد التقدم',
    statusNotStarted: 'لم يبدأ',
    attempts: (count) => `${count} محاولة`,
    fallbackTitle: (order) => `امتحان ${order}`,
  },
  quiz: {
    loading: 'جارٍ تحميل الاختبار...',
    notFound: 'تعذر العثور على هذا الامتحان.',
    invalidData: 'تم استلام بيانات اختبار غير صالحة.',
    loadError: 'تعذر تحميل بيانات الاختبار.',
    notLoggedIn: 'لم يتم تسجيل الدخول.',
    mediaLoading: 'جارٍ تحميل الوسائط...',
    remainingTime: 'الوقت المتبقي',
    showRemainingTime: 'إظهار الوقت المتبقي',
    hideRemainingTime: 'إخفاء الوقت المتبقي',
    examTools: 'أدوات الامتحان',
    enableSound: 'تشغيل الصوت',
    disableSound: 'إيقاف الصوت',
    volumeControl: 'التحكم في مستوى الصوت',
    volumeDown: 'خفض الصوت',
    volumeUp: 'رفع الصوت',
    changeFontSize: 'تغيير حجم النص',
    displaySettings: 'إعدادات العرض',
    liveAudioLanguage: 'لغة الصوت',
    liveAudioLanguageDescription: 'هذا الاختيار مستقل عن اللغات المستخدمة لعرض السؤال.',
    questionLayout: 'تخطيط السؤال',
    layoutSide: 'جنبًا إلى جنب',
    layoutStacked: 'واحد تحت الآخر',
    goToFirstQuestion: 'الانتقال إلى السؤال الأول',
    goToQuestion: (number) => `الانتقال إلى السؤال ${number}`,
    goToInfo: 'الانتقال إلى المعلومات',
    overview: 'نظرة عامة',
    overviewDescription: 'استخدم هذه الصفحة للانتقال بسرعة إلى سؤال.',
    examOverview: 'نظرة عامة على الامتحان',
    examOverviewDescription: 'يمكنك هنا معرفة الأسئلة التي أجبت عنها والتي لم تجب عنها والأسئلة التي وضعت عليها علامة.',
    currentQuestion: 'السؤال الحالي',
    questionDrawer: 'درج الأسئلة',
    questionDrawerDescription: 'استخدمه للتنقل بسرعة بين الأسئلة.',
    questionDrawerToggleHint: 'فتح وإغلاق درج الأسئلة',
    openQuestionDrawer: 'فتح درج الأسئلة',
    closeQuestionDrawer: 'إغلاق درج الأسئلة',
    marked: 'مُعلَّم',
    bookmarkGuidance: 'استخدم الإشارة المرجعية للعودة بسرعة إلى هذا السؤال لاحقًا.',
    unanswered: 'بلا إجابة',
    answered: 'تمت الإجابة',
    submitExam: 'تسليم الامتحان',
    closeExamTitle: 'إغلاق الامتحان؟',
    closeExamDescription: 'يمكنك حفظ تقدمك والمتابعة لاحقًا.',
    saveAndClose: 'حفظ وإغلاق',
    closeWithoutSaving: 'إغلاق دون حفظ',
    theoryB: 'النظري B',
    mark: 'وضع علامة',
    close: 'إغلاق',
    questionCount: (current, total) => `السؤال ${current}/${total}`,
    questionNumber: (current) => `السؤال ${current}`,
    questionOfTotal: (current, total) => `السؤال ${current} من ${total}`,
    multipleAnswers: 'يمكن أن تكون عدة إجابات صحيحة.',
    orderInstruction: 'اسحب الأرقام إلى المكان الصحيح. اضغط مطولًا على رقم موضوع لنقله.',
    orderInstructionClassic: 'اسحب الأرقام إلى المكان الصحيح (اضغط على رقم موضوع لإعادته).',
    orderAllPlaced: 'تم وضع جميع الأرقام',
    singleOrderInstruction: 'اسحب علامة الصح إلى المكان الصحيح. اضغط عليها مطولًا لنقلها.',
    singleOrderPlaced: 'تم وضع علامة الصح',
    numberInputLabel: 'إجابة رقمية',
    numberKeypadLabel: 'لوحة أرقام',
    numberBackspace: 'حذف الرقم الأخير',
    numberMoveKeypad: 'نقل لوحة الأرقام',
    numberMinus: 'إدخال علامة السالب',
    numberHideKeypad: 'إخفاء لوحة الأرقام',
    numberDecimalSeparator: '٫',
    dynamicPlayInstruction: 'اضغط على التشغيل لبدء الفيديو.',
    dynamicPlay: 'تشغيل الفيديو',
    dynamicPause: 'إيقاف الفيديو مؤقتًا',
    questionLoading: 'جارٍ تحميل السؤال...',
    previous: 'السابق',
    next: 'التالي',
    liveStartTitle: 'الاستعداد للامتحان المباشر',
    liveStartDescription: 'يبدأ الامتحان من السؤال الأول ويقرأ كل سؤال بصوت عالٍ تلقائيًا.',
    liveStart: 'بدء الامتحان المباشر',
    liveLoadingAudio: 'جارٍ تحميل الصوت...',
    liveNarrating: 'جارٍ قراءة السؤال بصوت عالٍ',
    liveAnswerTime: 'وقت الإجابة',
    liveCorrectAnswer: 'الإجابة الصحيحة',
    liveNextQuestion: 'السؤال التالي',
    livePaused: 'متوقف مؤقتًا',
    livePause: 'إيقاف مؤقت',
    liveResume: 'متابعة',
    liveAudioError: 'تعذر تحميل الصوت. تم إيقاف الامتحان المباشر مؤقتًا.',
    liveRetry: 'إعادة محاولة تشغيل الصوت',
    liveCloseDescription: 'لن يتم حفظ تقدمك أو إجاباتك.',
    liveCompleteTitle: 'اكتمل الامتحان المباشر',
    liveCompleteDescription: 'لقد أكملت جميع أسئلة الامتحان المباشر. لم يتم حفظ أي نتيجة.',
    liveRestart: 'إعادة البدء',
    submit: 'تسليم',
    info: 'معلومات',
    finalQuestionMessage: 'كان هذا هو السؤال الأخير في امتحانك.',
    submitNextScreenMessage: 'في الشاشة التالية ستسلّم الامتحان، ثم سترى ما إذا كنت قد نجحت.',
    confirmSubmitTitle: 'هل أنت متأكد أنك تريد تسليم الامتحان؟',
    submitTimeRemaining: (minutes, seconds) => `لديك ${minutes} دقيقة و${seconds} ثانية متبقية`,
    submitBookmarkCount: (count) => `وضعت ${count} إشارة مرجعية`,
    submitUnansweredCount: (count) => `لديك ${count} سؤال بلا إجابة`,
    returnToExam: 'العودة إلى الامتحان',
    submitDefinitely: 'تسليم نهائي',
    previousQuestion: 'السؤال السابق',
    nextQuestion: 'السؤال التالي',
  },
  result: {
    title: 'النتيجة',
    score: (correct, total) => `${correct}/${total} صحيحة`,
    passed: 'ناجح!',
    failed: 'راسب',
    notAnswered: 'لم تتم الإجابة',
    yourAnswer: 'إجابتك:',
    correctAnswer: 'الإجابة الصحيحة:',
    redoExam: 'إعادة الاختبار',
  },
  quizzes: {
    title: 'الاختبارات',
    syncDisabled: 'لم يتم تسجيل الدخول، مزامنة الخادم متوقفة.',
    incompleteLoadError: 'تعذر تحميل الاختبارات غير المكتملة.',
    progressResumed: (name) => `تمت متابعة المحاولة في «${name}».`,
    progressSaved: 'تم حفظ التقدم على الخادم.',
    submitted: (correct, total, percentage) => `تم التسليم! النتيجة: ${correct}/${total} (${percentage}٪).`,
    submitFailed: 'فشل التسليم إلى الخادم. تم إنهاء النتيجة محليًا.',
    continueWhereLeftOff: 'المتابعة من حيث توقفت',
  },
  downloads: {
    eyebrow: 'المواد',
    title: 'التنزيلات',
    description: 'ستظهر مواد الدراسة والملخصات هنا فور توفرها.',
    empty: 'لا توجد تنزيلات بعد. عد قريبًا للحصول على مواد إضافية.',
  },
  account: {
    eyebrow: 'الحساب',
    title: 'بياناتي',
    description: 'يمكنك أدناه رؤية بيانات المستخدم التي جمعناها.',
    loadError: 'تعذر تحميل بيانات الحساب.',
  },
  auth: {
    loginTitle: 'تسجيل الدخول',
    emailRequired: 'أدخل بريدك الإلكتروني',
    passwordRequired: 'أدخل كلمة المرور',
    forgotPassword: 'هل نسيت كلمة المرور؟',
    expiredAccess: 'انتهت صلاحية وصولك. اختر باقة للتجديد.',
    loginFailed: 'فشل تسجيل الدخول.',
    renewAccess: 'تجديد الوصول',
    busy: 'جارٍ التنفيذ...',
    forgotTitle: 'نسيت كلمة المرور',
    validEmailRequired: 'أدخل بريدًا إلكترونيًا صالحًا.',
    codeSent: 'تم إرسال رمز إلى بريدك الإلكتروني.',
    codeRequestError: 'حدث خطأ أثناء طلب الرمز.',
    send: 'إرسال',
    sending: 'جارٍ الإرسال...',
    enterCodeTitle: 'إدخال الرمز',
    codeLabel: 'رمز من 4 أرقام',
    newPassword: 'كلمة مرور جديدة',
    invalidCode: 'أدخل رمزًا صالحًا من 4 أرقام.',
    shortPassword: 'استخدم كلمة مرور من 4 أحرف على الأقل.',
    passwordChanged: 'تم تغيير كلمة المرور بنجاح!',
    resetError: 'الرمز غير صالح أو حدث خطأ أثناء تعيين كلمة المرور.',
    saving: 'جارٍ الحفظ...',
    setPassword: 'تعيين كلمة المرور',
  },
}

const zh: typeof nl = {
  common: {
    loading: '加载中...',
    retry: '重试',
    reload: '重新加载',
    cancel: '取消',
    back: '返回',
    start: '开始',
    save: '保存',
    confirm: '确认',
    email: '电子邮箱',
    password: '密码',
    package: '套餐',
    unknown: '未知',
    noPackage: '尚未购买套餐',
    notAvailable: '不可用',
    mediaUnavailable: '媒体不可用',
    quizQuestionAlt: (number) => `第 ${number} 题`,
  },
  nav: {
    dashboard: '首页',
    aiCoach: 'AI 学习教练',
    videoCourse: '视频课程',
    theoryBook: '理论教材',
    notes: '笔记',
    practiceExams: '模拟考试',
    trafficSigns: '交通标志',
    progress: '学习进度',
    account: '账户',
    orders: '订单',
    settings: '设置',
    downloads: '下载',
    result: '考试结果',
    logout: '退出登录',
    login: '登录',
    order: '购买',
    menu: '菜单',
    openMenu: '打开菜单',
    closeMenu: '关闭菜单',
    openNavigation: '打开导航',
    toggleNavigation: '切换导航',
    section: (title) => `${title}部分`,
    contact: '联系我们',
  },
  settings: {
    eyebrow: '设置',
    title: '设置',
    description: '调整考试、语言和显示偏好。你的选择会保存在此设备上。',
    examSection: '考试',
    extraTimeLabel: '延长时间（45 分钟）',
    extraTimeDescription: '考试时间由 30 分钟延长至 45 分钟，与 CBR 延时考试一致。',
    cbrStyleLabel: 'CBR 考试界面',
    cbrStyleDescription: '使用荷兰 CBR 官方理论考试的界面风格。',
    enhancedOrderControlsLabel: '新版拖放题操作',
    enhancedOrderControlsDescription: '拖动时显示目标位置，并允许移动或交换已放置的数字。',
    customNumberKeypadLabel: '数字题使用 CBR 键盘',
    customNumberKeypadDescription: '在 CBR 界面中使用浮动数字键盘代替标准数字输入框。',
    blueHotspotSelectionLabel: '热点题蓝色选中效果',
    blueHotspotSelectionDescription: '在 CBR 界面中以半透明蓝色突出显示所选图片。',
    blueSingleChoiceSelectionLabel: '单选题蓝色选中效果',
    blueSingleChoiceSelectionDescription: '在 CBR 界面中将整行所选答案显示为蓝色。',
    blueMultipleChoiceSelectionLabel: '多选题蓝色选中效果',
    blueMultipleChoiceSelectionDescription: '在 CBR 界面中将每个所选答案显示为蓝色并添加勾选标记。',
    videoAutoplayLabel: '自动播放视频',
    videoAutoplayDescription: '自动播放动态视频。关闭后需点击播放按钮开始。',
    languageSection: '语言',
    websiteLanguage: '网站语言',
    secondaryLanguageLabel: '主语言下方显示第二语言',
    secondaryLanguageDescription: '可选择在题目和答案下方显示荷兰语、英语、阿拉伯语或简体中文。',
    secondaryLanguageOff: '关闭（仅一种语言）',
    displaySection: '显示',
    darkThemeLabel: '深色主题',
    darkThemeDescription: '切换为深色显示模式。',
  },
  exams: {
    eyebrow: '模拟考试',
    title: '模拟考试',
    intro: '从这里开始驾照之路！选择一套试卷开始练习。',
    unavailableForPlan: '此语言下没有适用于你当前套餐的考试。',
    sessionExpired: '登录已过期，请重新登录。',
    loadError: '无法加载考试。',
    noToken: '没有可用的登录令牌',
    invalidData: '收到的考试数据格式异常。',
    examLabel: '考试',
    newExam: '新考试',
    planLabel: '套餐：',
    notAvailableTitle: '当前套餐不可用。',
    restart: '重新开始',
    resume: '继续',
    statsTotal: '总数',
    statsOpen: '未完成',
    statsPassed: '已通过',
    statsInProgress: '进行中',
    inProgress: (count) => `${count} 个进行中`,
    statusPassed: '已通过',
    statusFailed: '未通过',
    statusInProgress: '进行中',
    statusNotStarted: '尚未开始',
    attempts: (count) => `${count} 次尝试`,
    fallbackTitle: (order) => `考试 ${order}`,
  },
  quiz: {
    loading: '正在加载考试...',
    notFound: '找不到这套考试。',
    invalidData: '收到的考试数据无效。',
    loadError: '无法加载考试数据。',
    notLoggedIn: '尚未登录。',
    mediaLoading: '正在加载媒体...',
    remainingTime: '剩余时间',
    showRemainingTime: '显示剩余时间',
    hideRemainingTime: '隐藏剩余时间',
    examTools: '考试工具',
    enableSound: '开启声音',
    disableSound: '关闭声音',
    volumeControl: '音量控制',
    volumeDown: '降低音量',
    volumeUp: '提高音量',
    changeFontSize: '调整文字大小',
    displaySettings: '显示设置',
    liveAudioLanguage: '朗读语言',
    liveAudioLanguageDescription: '此选项与题目显示语言相互独立。',
    questionLayout: '题目布局',
    layoutSide: '左右排列',
    layoutStacked: '上下排列',
    goToFirstQuestion: '前往第 1 题',
    goToQuestion: (number) => `前往第 ${number} 题`,
    goToInfo: '前往说明',
    overview: '总览',
    overviewDescription: '使用总览快速跳转到指定题目。',
    examOverview: '考试总览',
    examOverviewDescription: '查看已回答、未回答和已标记的题目。',
    currentQuestion: '当前题目',
    questionDrawer: '题目列表',
    questionDrawerDescription: '使用列表在题目之间快速切换。',
    questionDrawerToggleHint: '打开或关闭题目列表',
    openQuestionDrawer: '打开题目列表',
    closeQuestionDrawer: '关闭题目列表',
    marked: '已标记',
    bookmarkGuidance: '使用书签可稍后快速返回本题。',
    unanswered: '未回答',
    answered: '已回答',
    submitExam: '提交考试',
    closeExamTitle: '关闭考试？',
    closeExamDescription: '你可以保存进度，稍后继续。',
    saveAndClose: '保存并关闭',
    closeWithoutSaving: '不保存并关闭',
    theoryB: 'B 类理论考试',
    mark: '标记',
    close: '关闭',
    questionCount: (current, total) => `第 ${current}/${total} 题`,
    questionNumber: (current) => `第 ${current} 题`,
    questionOfTotal: (current, total) => `第 ${current} 题，共 ${total} 题`,
    multipleAnswers: '可能有多个正确答案。',
    orderInstruction: '将数字拖到正确位置。长按已放置的数字可移动。',
    orderInstructionClassic: '将数字拖到正确位置（点击已放置的数字可将其收回）。',
    orderAllPlaced: '所有数字均已放置',
    singleOrderInstruction: '将勾选标记拖到正确位置。长按可移动。',
    singleOrderPlaced: '勾选标记已放置',
    numberInputLabel: '数字答案',
    numberKeypadLabel: '数字键盘',
    numberBackspace: '删除上一位数字',
    numberMoveKeypad: '移动键盘',
    numberMinus: '输入负号',
    numberHideKeypad: '隐藏键盘',
    numberDecimalSeparator: '.',
    dynamicPlayInstruction: '点击播放开始观看视频。',
    dynamicPlay: '播放视频',
    dynamicPause: '暂停视频',
    questionLoading: '正在加载题目...',
    previous: '上一题',
    next: '下一题',
    liveStartTitle: '准备 LIVE 考试',
    liveStartDescription: '考试从第 1 题开始，并自动朗读每道题。',
    liveStart: '开始 LIVE 考试',
    liveLoadingAudio: '正在加载音频...',
    liveNarrating: '正在朗读题目',
    liveAnswerTime: '答题时间',
    liveCorrectAnswer: '正确答案',
    liveNextQuestion: '下一题',
    livePaused: '已暂停',
    livePause: '暂停',
    liveResume: '继续',
    liveAudioError: '无法加载音频，LIVE 考试已暂停。',
    liveRetry: '重试音频',
    liveCloseDescription: '你的进度和答案不会被保存。',
    liveCompleteTitle: 'LIVE 考试已完成',
    liveCompleteDescription: '你已完成所有 LIVE 题目，本次结果不会保存。',
    liveRestart: '重新开始',
    submit: '提交',
    info: '说明',
    finalQuestionMessage: '这是本次考试的最后一题。',
    submitNextScreenMessage: '下一页将提交考试，之后你会看到是否通过。',
    confirmSubmitTitle: '确定要提交考试吗？',
    submitTimeRemaining: (minutes, seconds) => `你还有 ${minutes} 分 ${seconds} 秒`,
    submitBookmarkCount: (count) => `你标记了 ${count} 道题`,
    submitUnansweredCount: (count) => `你还有 ${count} 道题未回答`,
    returnToExam: '返回考试',
    submitDefinitely: '确认提交',
    previousQuestion: '上一题',
    nextQuestion: '下一题',
  },
  result: {
    title: '考试结果',
    score: (correct, total) => `答对 ${correct}/${total} 题`,
    passed: '通过！',
    failed: '未通过',
    notAnswered: '未回答',
    yourAnswer: '你的答案：',
    correctAnswer: '正确答案：',
    redoExam: '重新考试',
  },
  quizzes: {
    title: '考试',
    syncDisabled: '尚未登录，服务器同步已关闭。',
    incompleteLoadError: '无法加载未完成的考试。',
    progressResumed: (name) => `已继续“${name}”。`,
    progressSaved: '进度已保存到云端。',
    submitted: (correct, total, percentage) => `已提交！得分：${correct}/${total}（${percentage}%）。`,
    submitFailed: '服务器提交失败，结果已在本地完成。',
    continueWhereLeftOff: '从上次的位置继续',
  },
  downloads: {
    eyebrow: '学习资料',
    title: '下载',
    description: '课程资料和总结可用后会显示在这里。',
    empty: '暂时没有可下载内容，请稍后再来查看。',
  },
  account: {
    eyebrow: '账户',
    title: '我的资料',
    description: '你可以在下方查看我们保存的用户资料。',
    loadError: '无法加载账户资料。',
  },
  auth: {
    loginTitle: '登录',
    emailRequired: '请输入电子邮箱',
    passwordRequired: '请输入密码',
    forgotPassword: '忘记密码？',
    expiredAccess: '你的使用期限已结束，请选择套餐续费。',
    loginFailed: '登录失败。',
    renewAccess: '续费',
    busy: '处理中...',
    forgotTitle: '忘记密码',
    validEmailRequired: '请输入有效的电子邮箱。',
    codeSent: '验证码已发送到你的邮箱。',
    codeRequestError: '请求验证码时出现错误。',
    send: '发送',
    sending: '正在发送...',
    enterCodeTitle: '输入验证码',
    codeLabel: '4 位验证码',
    newPassword: '新密码',
    invalidCode: '请输入有效的 4 位验证码。',
    shortPassword: '密码至少需要 4 个字符。',
    passwordChanged: '密码修改成功。',
    resetError: '验证码无效或设置密码时出错。',
    saving: '正在保存...',
    setPassword: '设置密码',
  },
}

export const translations = { nl, en, ar, zh }
export type Translation = typeof nl
